import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  useToast,
} from "@chakra-ui/react";
import VmsPageCard from "../components/VmsPageCard";
import { vmsApi } from "../services/vmsApi";

const VmsReportsSummary = () => {
  const [filters, setFilters] = useState({ office_id: "", start_date: "", end_date: "", format: "csv" });
  const [offices, setOffices] = useState([]);
  const [summary, setSummary] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToast();

  const loadSummary = async (nextFilters = filters) => {
    setIsLoading(true);
    try {
      const data = await vmsApi.getSummaryReport(nextFilters);
      setSummary(data?.data || data || {});
    } catch (err) {
      toast({ title: "Failed to load summary", description: err?.message, status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const officeData = await vmsApi.getOffices();
      setOffices(Array.isArray(officeData) ? officeData : officeData?.data || []);
      await loadSummary();
    };
    init().catch(() => {});
  }, []);

  const exportReport = async () => {
    setIsExporting(true);
    try {
      const blob = await vmsApi.getExportReport(filters);
      const extension = filters.format || "csv";
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `vms-report.${extension}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: "Export failed", description: err?.message, status: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <VmsPageCard title="Summary Analytics">
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
        <FormControl>
          <FormLabel>Office</FormLabel>
          <Select
            value={filters.office_id}
            onChange={(e) => setFilters({ ...filters, office_id: e.target.value })}
          >
            <option value="">All</option>
            {offices.map((office) => {
              const id = office.id || office.office_id;
              return (
                <option key={id} value={id}>
                  {office.name || id}
                </option>
              );
            })}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Start Date</FormLabel>
          <Input
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
          />
        </FormControl>
        <FormControl>
          <FormLabel>End Date</FormLabel>
          <Input
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Export Format</FormLabel>
          <Select value={filters.format} onChange={(e) => setFilters({ ...filters, format: e.target.value })}>
            <option value="csv">csv</option>
            <option value="json">json</option>
          </Select>
        </FormControl>
      </SimpleGrid>

      <HStack mt={4}>
        <Button colorScheme="green" onClick={() => loadSummary()} isLoading={isLoading}>
          Refresh Summary
        </Button>
        <Button variant="outline" onClick={exportReport} isLoading={isExporting}>
          Export
        </Button>
      </HStack>

      <SimpleGrid mt={5} columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
        <Stat bg="gray.50" p={4} borderRadius="md">
          <StatLabel>Total Visits</StatLabel>
          <StatNumber>{summary.total_visits ?? 0}</StatNumber>
        </Stat>
        <Stat bg="gray.50" p={4} borderRadius="md">
          <StatLabel>Approved</StatLabel>
          <StatNumber>{summary.approved ?? 0}</StatNumber>
        </Stat>
        <Stat bg="gray.50" p={4} borderRadius="md">
          <StatLabel>Declined</StatLabel>
          <StatNumber>{summary.declined ?? 0}</StatNumber>
        </Stat>
        <Stat bg="gray.50" p={4} borderRadius="md">
          <StatLabel>Expired</StatLabel>
          <StatNumber>
            {summary.expired ?? summary.status_breakdown?.EXPIRED ?? summary.status_breakdown?.expired ?? 0}
          </StatNumber>
        </Stat>
      </SimpleGrid>
    </VmsPageCard>
  );
};

export default VmsReportsSummary;
